// Updated SocialMediaManagerView.js - Works with new variant-aware ApprovalDropdown

import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../utils/api";
import SchedulePostDialog from "./SchedulePostDialog";
import ApprovalDropdown from "./ApprovalDropdown";
import AdditionalSocialPostActions from "./AdditionalSocialPostActions";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const SocialMediaManagerView = () => {
  const [allPosts, setAllPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [providerFilter, setProviderFilter] = useState("all");
  const [editingPost, setEditingPost] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [postToSchedule, setPostToSchedule] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [reSmackLoading, setReSmackLoading] = useState({});

  useEffect(() => {
    fetchAllPosts();
  }, []);

  useEffect(() => {
    filterPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPosts, statusFilter, providerFilter, searchQuery]);

  const fetchAllPosts = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all episodes to have episode metadata
      const episodesResponse = await apiGet('/getAllEpisodes');
      const episodes = episodesResponse.episodes || [];
      
      // Create a lookup map for episode details
      const episodeMap = {};
      episodes.forEach(episode => {
        const parts = episode.feedId_episodeId?.split('#') || [];
        const episodeId = parts.length > 1 ? parts[1] : episode.episodeId;
        
        episodeMap[episodeId] = {
          title: episode.title,
          feedId: episode.feedId,
          episodeId: episodeId,
        };
      });

      // Fetch all social posts
      const postsResponse = await apiGet('/getAllSocialPosts');
      let allPosts = postsResponse.posts || [];
      
      // Enrich posts with episode info from the lookup map
      allPosts = allPosts.map(post => {
        const parts = post.podcastId.split('#');
        const episodeId = parts.length > 2 ? parts[2] : (parts.length === 2 ? parts[1] : parts[0]);
        
        const episodeInfo = episodeMap[episodeId] || {};
        
        return {
          ...post,
          episodeTitle: episodeInfo.title || `Episode ${episodeId}`,
          episodeId: episodeId,
          feedId: episodeInfo.feedId
        };
      });
      
      setAllPosts(allPosts);
    } catch (error) {
      console.error("Error fetching social posts:", error);
      setError("Failed to load social posts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = [...allPosts];

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(post => post.status === statusFilter);
    }

    // Filter by provider
    if (providerFilter !== "all") {
      filtered = filtered.filter(post => post.provider === providerFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.content?.toLowerCase().includes(query) ||
        post.episodeTitle?.toLowerCase().includes(query) ||
        post.provider?.toLowerCase().includes(query)
      );
    }

    setFilteredPosts(filtered);
  };

  const startEditing = (post) => {
    setEditingPost(post);
    setEditContent(post.content);
  };

  const cancelEditing = () => {
    setEditingPost(null);
    setEditContent("");
  };

  const updatePost = async (post, action, additionalData = {}) => {
    try {
      // Construct provider key - prefer providerKey, reconstruct if missing
      // The format should be "platform#variantNumber" (e.g., "instagram#1")
      const providerValue = post.providerKey ||
        `${post.providerPlatform || post.provider}#${post.variantNumber || 1}`;

      let updateData = {
        podcastId: post.podcastId,
        provider: providerValue,
        ...additionalData
      };

      if (action === "edit") {
        updateData.content = editContent;
      }

      await apiPost('/updateSocialPost', updateData);
      await fetchAllPosts();
      
      if (action === "edit") {
        cancelEditing();
      }
    } catch (error) {
      console.error(`Error ${action}ing post:`, error);
      setError(`Failed to ${action} post. Please try again.`);
    }
  };

  /**
   * Calculate default scheduled date based on variant number
   * Variant 1: Immediate (now)
   * Variant 2: 3 days from now at 12:00 PM
   * Variant 3: 10 days from now at 12:00 PM
   */
  const getDefaultScheduledDate = (variantNumber) => {
    const today = new Date();
    
    switch(variantNumber) {
      case 1:
        // Variant 1: immediate posting (use current time)
        return today;
        
      case 2:
        // Variant 2: 3 days from now at 12:00 PM
        const threeDaysOut = new Date(today);
        threeDaysOut.setDate(today.getDate() + 3);
        threeDaysOut.setHours(12, 0, 0, 0);
        return threeDaysOut;
        
      case 3:
        // Variant 3: 10 days from now at 12:00 PM
        const tenDaysOut = new Date(today);
        tenDaysOut.setDate(today.getDate() + 10);
        tenDaysOut.setHours(12, 0, 0, 0);
        return tenDaysOut;
        
      default:
        return today;
    }
  };

  // Updated handlePostNow to accept variantNumber and schedule accordingly
  const handlePostNow = async (post, variantNumber = 1) => {
    try {
      const scheduledDate = getDefaultScheduledDate(variantNumber);

      // Construct provider key - prefer providerKey, reconstruct if missing
      const providerValue = post.providerKey ||
        `${post.providerPlatform || post.provider}#${post.variantNumber || 1}`;

      const updateData = {
        podcastId: post.podcastId,
        provider: providerValue,
        scheduledTime: scheduledDate.toISOString(),
        isApproved: true,
        status: 'scheduled',
        immediatePosting: variantNumber === 1  // Only variant 1 posts immediately
      };

      await apiPost('/updateSocialPost', updateData);
      await fetchAllPosts();
      
      setError(null);
    } catch (error) {
      console.error("Error scheduling post:", error);
      setError("Failed to schedule post. Please try again.");
    }
  };

  const handleCustomSchedule = async (post) => {
    setPostToSchedule(post);
    setShowScheduleDialog(true);
  };

  const handleSchedulePost = async (scheduledDateTime) => {
    if (!postToSchedule) return;

    try {
      // Construct provider key - prefer providerKey, reconstruct if missing
      const providerValue = postToSchedule.providerKey ||
        `${postToSchedule.providerPlatform || postToSchedule.provider}#${postToSchedule.variantNumber || 1}`;

      const updateData = {
        podcastId: postToSchedule.podcastId,
        provider: providerValue,
        scheduledTime: scheduledDateTime.toISOString(),
        isApproved: true,
        status: 'scheduled',
      };

      await apiPost('/updateSocialPost', updateData);
      await fetchAllPosts();
      
      setShowScheduleDialog(false);
      setPostToSchedule(null);
      setError(null);
    } catch (error) {
      console.error("Error scheduling post:", error);
      setError("Failed to schedule post. Please try again.");
    }
  };

  // Handle ReSmack action
  const handleReSmack = async (post) => {
    const postKey = post.providerKey || `${post.provider}-${post.podcastId}`;

    try {
      setReSmackLoading(prev => ({ ...prev, [postKey]: true }));
      setError(null);

      // Construct provider key - prefer providerKey, reconstruct if missing
      const providerValue = post.providerKey ||
        `${post.providerPlatform || post.provider}#${post.variantNumber || 1}`;

      await apiPost('/regenerateSocialPost', {
        podcastId: post.podcastId,
        provider: providerValue,
        variantNumber: post.variantNumber || 1
      });

      await fetchAllPosts();
      
      setError(null);
    } catch (error) {
      console.error("Error regenerating post:", error);
      setError("Failed to regenerate post. Please try again.");
    } finally {
      setReSmackLoading(prev => ({ ...prev, [postKey]: false }));
    }
  };

  // Handle Archive action
  const handleArchive = async (post) => {
    try {
      await updatePost(post, 'archive', { status: 'archived' });
      setError(null);
    } catch (error) {
      console.error("Error archiving post:", error);
      setError("Failed to archive post. Please try again.");
    }
  };

  // Handle Unarchive action
  const handleUnarchive = async (post) => {
    try {
      await updatePost(post, 'unarchive', { status: 'pending' });
      setError(null);
    } catch (error) {
      console.error("Error unarchiving post:", error);
      setError("Failed to unarchive post. Please try again.");
    }
  };

  // Handle Repost Now action - posts immediately
  const handleRepostNow = async (post) => {
    try {
      const now = new Date();

      // Construct provider key - prefer providerKey, reconstruct if missing
      const providerValue = post.providerKey ||
        `${post.providerPlatform || post.provider}#${post.variantNumber || 1}`;

      const updateData = {
        podcastId: post.podcastId,
        provider: providerValue,
        scheduledTime: now.toISOString(),
        isApproved: true,
        status: 'scheduled',
        immediatePosting: true
      };

      await apiPost('/updateSocialPost', updateData);
      await fetchAllPosts();
      
      setError(null);
    } catch (error) {
      console.error("Error reposting immediately:", error);
      setError("Failed to repost immediately. Please try again.");
    }
  };

  // Handle Repost Schedule action - opens schedule dialog
  const handleRepostSchedule = (post) => {
    setPostToSchedule(post);
    setShowScheduleDialog(true);
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "social-post-status-pending",
      scheduled: "social-post-status-scheduled",
      published: "social-post-status-published",
      failed: "social-post-status-failed",
      archived: "social-post-status-archived"
    };

    return (
      <span className={`social-post-status-badge ${statusStyles[status] || "social-post-status-unknown"}`} id={`smStatusBadge-${status}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getProviderIcon = (provider) => {
    const iconStyles = "social-provider-icon";
    const iconSize = "16";
    
    switch (provider) {
      case "facebook":
        return (
          <span className={`${iconStyles} social-provider-facebook`} id={`smProviderIcon-facebook`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          </span>
        );
      case "instagram":
        return (
          <span className={`${iconStyles} social-provider-instagram`} id={`smProviderIcon-instagram`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/>
            </svg>
          </span>
        );
      case "linkedin":
        return (
          <span className={`${iconStyles} social-provider-linkedin`} id={`smProviderIcon-linkedin`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </span>
        );
      case "x":
      case "twitter":
        return (
          <span className={`${iconStyles} social-provider-twitter`} id={`smProviderIcon-twitter`}>
            <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </span>
        );
      default:
        return null;
    }
  };

  /**
 * Helper function to render the publish info box
 * Shows scheduled/published date info based on post status and variant
 */
  const renderPublishInfoBox = (post) => {
    // Only show for pending and published posts
    if (post.status !== 'pending' && post.status !== 'published') {
      return null;
    }

    const variantNumber = post.variantNumber || 1;
    
    if (post.status === 'published') {
      // For published posts, show when it was published
      const publishDate = post.publishedAt || post.scheduledTime;
      if (!publishDate) return null;
      
      const date = new Date(publishDate);
      const dateStr = date.toLocaleDateString('en-US', { 
        month: '2-digit', 
        day: '2-digit', 
        year: '2-digit' 
      });
      
      return (
        <div className="publish-info-box" id={`publishInfoBox-${post.providerKey || post.provider}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Published on {dateStr}
        </div>
      );
    }
    
    // For pending posts, show default scheduled date based on variant
    const getDefaultScheduledDate = (variant) => {
      const today = new Date();
      switch(variant) {
        case 1:
          return today;
        case 2:
          const threeDaysOut = new Date(today);
          threeDaysOut.setDate(today.getDate() + 3);
          threeDaysOut.setHours(12, 0, 0, 0);
          return threeDaysOut;
        case 3:
          const tenDaysOut = new Date(today);
          tenDaysOut.setDate(today.getDate() + 10);
          tenDaysOut.setHours(12, 0, 0, 0);
          return tenDaysOut;
        default:
          return today;
      }
    };
    
    const scheduledDate = getDefaultScheduledDate(variantNumber);
    
    if (variantNumber === 1) {
      return (
        <div className="publish-info-box" id={`publishInfoBox-${post.providerKey || post.provider}`}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Set to publish immediately
        </div>
      );
    }
    
    const dateStr = scheduledDate.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: '2-digit' 
    });
    
    return (
      <div className="publish-info-box" id={`publishInfoBox-${post.providerKey || post.provider}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
        Set to publish on {dateStr}
      </div>
    );
  };

  const calendarEvents = allPosts
  .filter(post =>  (post.status === 'scheduled' || post.status === 'published') && post.scheduledTime) // Only show posts with scheduled times
  .map(post => ({
    id: post.providerKey || `${post.podcastId}-${post.provider}`,
    title: `${post.provider} - ${post.status}`,
    start: new Date(post.scheduledTime),
    end: new Date(post.scheduledTime),
    resource: post // Store the full post data for access later
  }));

  console.log('Total posts:', allPosts.length);
  console.log('Posts with scheduledTime:', allPosts.filter(p => p.scheduledTime).length);
  console.log('Scheduled/Published posts:', allPosts.filter(p => p.status === 'scheduled' || p.status === 'published').length);
  console.log('Calendar events:', calendarEvents.length);
  console.log('Calendar events data:', calendarEvents);


  const eventStyleGetter = (event) => {
    const post = event.resource;
    let backgroundColor = '#3174ad'; // default blue
    
    // Color by platform
    switch(post.provider) {
      case 'facebook':
        backgroundColor = '#1877f2';
        break;
      case 'instagram':
        backgroundColor = '#E4405F';
        break;
      case 'linkedin':
        backgroundColor = '#0A66C2';
        break;
      case 'x':
      case 'twitter':
        backgroundColor = '#000000';
        break;
      default:
        break;
    }
    
    // Adjust opacity for status
    if (post.status === 'published') {
      backgroundColor += '80'; // 50% opacity
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  if (loading) return <div className="social-media-manager-loading" id="smLoading">Loading posts...</div>;
  if (error && !allPosts.length) return <div className="social-media-manager-error" id="smError">{error}</div>;

  return (
    <div className="social-media-manager-container" id="smContainer">
      <div className="social-media-manager-header" id="smHeader">
        <h2 className="social-media-manager-title" id="smTitle">Social Media Manager</h2>
      </div>

      {/* NEW: Calendar View */}
      <div className="social-media-calendar-container">
        <Calendar
          localizer={localizer}
          events={calendarEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 500, marginBottom: '2rem' }}
          views={['month', 'week', 'day', 'agenda']}
          defaultView="month"
          eventPropGetter={eventStyleGetter}
          onSelectEvent={(event) => {
            // Optional: Handle clicking on an event
            console.log('Selected event:', event.resource);
          }}
        />
      </div>

      {/* Filters */}
      <div className="social-media-manager-filters" id="smFilters">
        <div className="filter-group" id="smFilterGroup">
          <label htmlFor="statusFilter" className="filter-label">Status:</label>
          <select
            id="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
            <option value="failed">Failed</option>
            <option value="archived">Archived</option>
          </select>

          <label htmlFor="providerFilter" className="filter-label">Platform:</label>
          <select
            id="providerFilter"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="facebook">Facebook</option>
            <option value="instagram">Instagram</option>
            <option value="linkedin">LinkedIn</option>
            <option value="x">X</option>
          </select>

          <input
            type="text"
            placeholder="Search posts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            id="smSearchInput"
          />
        </div>
      </div>

      {error && <div className="social-media-manager-error-message" id="smErrorMessage">{error}</div>}

      {filteredPosts.length === 0 ? (
        <div className="social-media-manager-empty" id="socialMediaManagerEmpty">
          No posts found matching your filters.
        </div>
      ) : (
        <div className="social-media-manager-posts" id="socialMediaManagerPosts">
          {filteredPosts.map((post, index) => (
            <div 
              key={post.providerKey || `${post.podcastId}-${post.provider}`}
              className={`social-post-card ${post.status === 'archived' ? 'archived' : ''}`}
              id={`socialMediaPost-${post.providerKey || post.provider}-${index}`}
            >
              <div className="social-post-header" id={`socialMediaPostHeader-${post.providerKey || post.provider}-${index}`}>
                <div className="social-post-provider" id={`socialMediaPostProvider-${post.providerKey || post.provider}-${index}`}>
                  {getProviderIcon(post.provider)}
                  <div className="social-post-provider-info" id={`socialMediaPostProviderInfo-${post.providerKey || post.provider}-${index}`}>
                    <h4 className="social-post-provider-name" id={`socialMediaPostProviderName-${post.providerKey || post.provider}-${index}`}>
                      {post.provider.charAt(0).toUpperCase() + post.provider.slice(1)}
                    </h4>
                    <p className="social-post-episode-title" id={`socialMediaPostEpisodeTitle-${post.providerKey || post.provider}-${index}`}>
                      {post.episodeTitle}
                    </p>
                  </div>
                  <div className="social-post-status" id={`socialMediaPostStatus-${post.providerKey || post.provider}-${index}`}>
                    {getStatusBadge(post.status)}
                  </div>
                </div>
                
                {post.scheduledTime && post.status === 'scheduled' && (
                  <div className="social-post-scheduled-time" id={`socialMediaPostScheduled-${post.providerKey || post.provider}-${index}`}>
                    Scheduled: {new Date(post.scheduledTime).toLocaleDateString()} at {new Date(post.scheduledTime).toLocaleTimeString()}
                  </div>
                )}
              </div>
              
              {(editingPost?.providerKey && editingPost.providerKey === post.providerKey) || (editingPost?.provider === post.provider && editingPost?.podcastId === post.podcastId) ? (
                // Edit Mode
                <div className="social-post-edit-form" id={`socialMediaPostEditForm-${post.providerKey || post.provider}-${index}`}>
                  <textarea
                    className="social-post-edit-textarea"
                    id={`socialMediaPostEditTextarea-${post.providerKey || post.provider}-${index}`}
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                  />
                  
                  <div className="social-post-edit-actions" id={`socialMediaPostEditActions-${post.providerKey || post.provider}-${index}`}>
                    <button
                      onClick={() => updatePost(post, "edit")}
                      className="social-post-save-btn"
                      id={`socialMediaPostSaveBtn-${post.providerKey || post.provider}-${index}`}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingPost(null)}
                      className="social-post-cancel-btn"
                      id={`socialMediaPostCancelBtn-${post.providerKey || post.provider}-${index}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode with new actions
                <div className="social-post-content-container" id={`socialMediaPostContent-${post.providerKey || post.provider}-${index}`}>
                  <p className="social-post-content" id={`socialMediaPostText-${post.providerKey || post.provider}-${index}`}>{post.content}</p>
                  
                <div className="social-post-actions" id={`socialMediaPostActions-${post.providerKey || post.provider}-${index}`}>
                  {/* Updated ApprovalDropdown for pending posts - removed onAutoSchedule and allPosts props */}
                  {post.status === 'pending' && (
                    <ApprovalDropdown
                      post={post}
                      onPostNow={handlePostNow}
                      onCustomSchedule={handleCustomSchedule}
                    />
                  )}
                  
                  {/* Add reschedule button for scheduled posts (existing functionality) */}
                  {post.status === 'scheduled' && (
                    <button
                      onClick={() => {
                        setPostToSchedule(post);
                        setShowScheduleDialog(true);
                      }}
                      className="social-post-reschedule-btn"
                      id={`socialPostRescheduleBtn-${post.providerKey || post.provider}-${index}`}
                    >
                      Reschedule
                    </button>
                  )}
                  
                  {/* New additional actions for all posts */}
                  <AdditionalSocialPostActions
                    post={post}
                    onEdit={startEditing}
                    onReSmack={handleReSmack}
                    onArchive={handleArchive}
                    onUnarchive={handleUnarchive}
                    onRepostNow={handleRepostNow}
                    onRepostSchedule={handleRepostSchedule}
                    isReSmackLoading={reSmackLoading[post.providerKey || `${post.provider}-${post.podcastId}`] || false}
                  />
                </div>
                </div>
              )}
              {renderPublishInfoBox(post, index)}
            </div>
          ))}
        </div>
      )}

      {/* Schedule Dialog */}
      <SchedulePostDialog
        isOpen={showScheduleDialog}
        onClose={() => {
          setShowScheduleDialog(false);
          setPostToSchedule(null);
        }}
        onSchedule={handleSchedulePost}
        initialDate={new Date()}
        initialTime="12:00"
      />
    </div>
  );
};

export default SocialMediaManagerView;